import {
  Button,
  Typography,
  Space,
  message,
  Descriptions,
  Image,
  Input,
  Dropdown,
  Menu,
} from "antd";
import { executeLambda } from "helpers/lambda";
import { updateProcurementStageItem } from "apis";
import { useState, useEffect } from "react";
import { buildStageSK, PROCUREMENT_STAGES } from "types";

const { Text, Link } = Typography;
const { TextArea } = Input;

const JiraTracker = ({ PK, stageData, setStageData, initialKey }) => {
  const [jiraKey, setJiraKey] = useState(initialKey || null);
  const [loading, setLoading] = useState(false);
  const [jiraDetails, setJiraDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [transitionLoading, setTransitionLoading] = useState(false);

  // Sync jiraKey with initialKey if initialKey changes and jiraKey is null
  useEffect(() => {
    if (initialKey && !jiraKey) {
      setJiraKey(initialKey);
    }
  }, [initialKey, jiraKey]);

  // Fetch JIRA ticket details and transitions when jiraKey changes
  useEffect(() => {
    const fetchJiraDetails = async () => {
      if (!jiraKey) {
        setJiraDetails(null);
        setTransitions([]);
        return;
      }

      setLoadingDetails(true);
      try {
        const response = await executeLambda(
          process.env.REACT_APP_LAMBDA_JIRA_API || "",
          { key: jiraKey }
        );

        if (response.success) {
          const body =
            typeof response.data.body === "string"
              ? JSON.parse(response.data.body)
              : response.data.body;

          // Expecting combined response: { issue: {...}, transitions: [...] }
          if (body?.issue?.fields) {
            setJiraDetails(body.issue);
            setTransitions(body.transitions || []);
          } else if (body?.fields) {
            // fallback if only issue returned
            setJiraDetails(body);
            setTransitions([]);
          } else {
            setJiraDetails(null);
            setTransitions([]);
            message.warning("JIRA details not found.");
          }
        } else {
          setJiraDetails(null);
          setTransitions([]);
          message.error("Failed to fetch JIRA details.");
        }
      } catch (err) {
        console.error("Error fetching JIRA details:", err);
        setJiraDetails(null);
        setTransitions([]);
        message.error("Error fetching JIRA details.");
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchJiraDetails();
  }, [jiraKey]);

  const buildBaseStage = () => ({
    PK: { S: PK },
    SK: { S: buildStageSK(PROCUREMENT_STAGES.Network) },
    stage_name: { S: "Network" },
    entity_type: { S: "ProcurementStage" },
  });

  const handleTrackWithJira = async () => {
    setLoading(true);
    try {
      const payload = {
        fields: {
          project: { id: "54559", key: "MSP" },
          summary: `New network request for ${PK}`,
          issuetype: { name: "Story" },
          labels: ["network", "automation"],
          description: `Automatically created JIRA ticket for order with PK: ${PK}`,
        },
      };

      const response = await executeLambda(
        process.env.REACT_APP_LAMBDA_JIRA_API || "",
        payload
      );

      if (response.success) {
        const body =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        if (body?.key) {
          const newJiraKey = body.key;
          setJiraKey(newJiraKey);

          const updatedStage = {
            ...(stageData || buildBaseStage()),
            jira_key: newJiraKey,
          };

          const updateResponse = await updateProcurementStageItem(updatedStage);
          if (updateResponse.success) {
            setStageData(updatedStage);
            message.success(`JIRA ticket ${newJiraKey} created and saved`);
          } else {
            message.warning(`JIRA created (${newJiraKey}) but failed to save`);
          }
        } else {
          message.error("JIRA key not returned.");
        }
      } else {
        message.error("Failed to create JIRA ticket.");
      }
    } catch (err) {
      console.error("JIRA creation error:", err);
      message.error("Error creating JIRA ticket.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      message.warning("Please enter a comment before submitting.");
      return;
    }
    setCommentLoading(true);
    try {
      const payload = {
        key: jiraKey,
        comment: commentText.trim(),
      };

      const response = await executeLambda(
        process.env.REACT_APP_LAMBDA_JIRA_API || "",
        payload
      );

      if (response.success) {
        message.success("Comment added successfully.");
        setCommentText("");
        // Refresh JIRA details to show new comment
        const refreshed = await executeLambda(
          process.env.REACT_APP_LAMBDA_JIRA_API || "",
          { key: jiraKey }
        );
        if (refreshed && refreshed.data) {
          const body =
            typeof refreshed.data.body === "string"
              ? JSON.parse(refreshed.data.body)
              : refreshed.data.body;
          setJiraDetails(body.issue || body);
          setTransitions(body.transitions || []);
        } else {
          message.warning("Could not refresh JIRA details after comment.");
        }
      } else {
        message.error("Failed to add comment.");
      }
    } catch (err) {
      console.error("Error adding comment:", err);
      message.error("Error adding comment.");
    } finally {
      setCommentLoading(false);
    }
  };

  const handleTransitionClick = async ({ key }) => {
    if (!jiraKey) return;
    setTransitionLoading(true);
    try {
      const payload = {
        key: jiraKey,
        transition: key,
      };

      const response = await executeLambda(
        process.env.REACT_APP_LAMBDA_JIRA_API || "",
        payload
      );

      if (response.success) {
        message.success("Transition successful.");
        // Refresh details and transitions
        const refreshed = await executeLambda(
          process.env.REACT_APP_LAMBDA_JIRA_API || "",
          { key: jiraKey }
        );
        if (refreshed && refreshed.data) {
          const body =
            typeof refreshed.data.body === "string"
              ? JSON.parse(refreshed.data.body)
              : refreshed.data.body;
          setJiraDetails(body.issue || body);
          setTransitions(body.transitions || []);
        }
      } else {
        message.error("Failed to perform transition.");
      }
    } catch (err) {
      console.error("Error performing transition:", err);
      message.error("Error performing transition.");
    } finally {
      setTransitionLoading(false);
    }
  };

  const labelStyle = {
    backgroundColor: "#f0f0f0",
    fontWeight: "600",
  };

  const contentStyle = {
    backgroundColor: "#fff",
    color: "#000",
  };

  const renderJiraDetails = () => {
    if (!jiraDetails) return null;

    const {
      key,
      fields: {
        summary,
        description,
        status,
        priority,
        reporter,
        created,
        updated,
        project,
        issuetype,
      },
    } = jiraDetails;

    return (
      <Descriptions
        title={`JIRA Ticket Details (${key})`}
        bordered
        size="small"
        column={1}
        style={{
          maxWidth: 600,
          marginTop: 16,
        }}
      >
        <Descriptions.Item label="Summary" labelStyle={labelStyle} contentStyle={contentStyle}>
          {summary}
        </Descriptions.Item>
        <Descriptions.Item label="Description" labelStyle={labelStyle} contentStyle={contentStyle}>
          {description || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Status" labelStyle={labelStyle} contentStyle={contentStyle}>
          {status?.name || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Priority" labelStyle={labelStyle} contentStyle={contentStyle}>
          {priority?.name || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Reporter" labelStyle={labelStyle} contentStyle={contentStyle}>
          {reporter?.displayName || "N/A"}
          {reporter?.avatarUrls?.["48x48"] && (
            <Image
              src={reporter.avatarUrls["48x48"]}
              alt={reporter.displayName}
              width={24}
              height={24}
              preview={false}
              style={{ marginLeft: 8, verticalAlign: "middle", borderRadius: "50%" }}
            />
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Created" labelStyle={labelStyle} contentStyle={contentStyle}>
          {new Date(created).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="Updated" labelStyle={labelStyle} contentStyle={contentStyle}>
          {new Date(updated).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label="Project" labelStyle={labelStyle} contentStyle={contentStyle}>
          {project?.name || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Issue Type" labelStyle={labelStyle} contentStyle={contentStyle}>
          {issuetype?.name || "N/A"}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  const renderComments = () => {
    if (!jiraDetails?.fields?.comment?.comments?.length) return null;

    const comments = jiraDetails.fields.comment.comments;

    return (
      <div
        style={{
          backgroundColor: "#fafafa",
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          padding: 12,
          maxHeight: 300,
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          fontFamily: "Arial, sans-serif",
          fontSize: 14,
          color: "#333",
        }}
      >
        <h3 style={{ marginBottom: 12 }}>Comments ({comments.length})</h3>
        {comments.map((comment) => (
          <div
            key={comment.id}
            style={{
              marginBottom: 12,
              borderBottom: "1px solid #e8e8e8",
              paddingBottom: 8,
            }}
          >
            <strong>{comment.author?.displayName || "Unknown"}</strong>
            <p style={{ marginTop: 4 }}>{comment.body}</p>
          </div>
        ))}
      </div>
    );
  };

  const transitionsMenu = (
    <Menu onClick={handleTransitionClick}>
      {transitions.length === 0 && (
        <Menu.Item disabled>No transitions available</Menu.Item>
      )}
      {transitions.map((t) => (
        <Menu.Item key={t.id}>{t.name}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {!jiraKey ? (
        <Button type="primary" onClick={handleTrackWithJira} loading={loading}>
          Track this order with JIRA
        </Button>
      ) : (
        <>
          <Text strong>
            Ticket Created:{" "}
            <Link
              href={`https://issues-testing.merck.com/browse/${jiraKey}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {jiraKey}
            </Link>
          </Text>

          {loadingDetails ? (
            <Text>Loading JIRA details...</Text>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 24,
                marginTop: 16,
                flexWrap: "wrap",
                maxWidth: "100%",
              }}
            >
              {/* Left column: JIRA Details */}
              <div style={{ flex: "0 0 600px", minWidth: 300 }}>
                {renderJiraDetails()}
              </div>

              {/* Right column: Comments + Add Comment + Transitions */}
              <div style={{ flex: "1 1 400px", minWidth: 300, maxWidth: 600 }}>
                <Dropdown overlay={transitionsMenu} trigger={['click']}>
                  <Button loading={transitionLoading} style={{ marginBottom: 16 }}>
                    Change Status of the issue
                  </Button>
                </Dropdown>

                {renderComments()}

                <div style={{ marginTop: 24 }}>
                  <TextArea
                    rows={4}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Enter your comment here"
                    maxLength={1000}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    loading={commentLoading}
                    style={{ marginTop: 8 }}
                    disabled={!commentText.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Space>
  );
};

export default JiraTracker;
